import { Component, HostListener, OnInit } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { ApplicationPathConstants } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { DateFormatter } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { DateUtil } from '@ddt-web/generic-utils';
import { DefaultScrollableHoldingsTableConfig } from '@ddt-web/holdings';
import { UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import { combineLatest, Observable, of } from 'rxjs';

import { ErrorCodes } from '../enums/error-codes.enum';
import { ModelsBrowseDataService } from '../services/models-browse-data.service';
import { ModelsBrowseLinkActionsService } from '../services/models-browse-link-actions.service';
import { ModelsBrowseModelsMappingService } from '../services/models-browse-models-mapping.service';

@UntilDestroy()
@Component({
	selector: 'mb-family-latest-holdings',
	templateUrl: './family-latest-holdings.component.html',
	styleUrls: ['./family-latest-holdings.component.scss']
})
export class FamilyLatestHoldingsComponent implements OnInit {

	@InjectConfig(ModelsExperienceConfigKey.SHOW_FUND_PERF_TABLE_LINK) private readonly showFundPerfTableLink = true;
	@InjectConfig(ModelsExperienceConfigKey.LATEST_HOLDINGS_HEADER_COLUMNS) private readonly latestHoldingsHeaderColumns = [];
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PORTFOLIO_LINKS) private readonly enablePortfolioLinks = true;
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.HIDE_LATEST_ALLOCATION_FAMILY_LIST) private readonly hideLatestAllocationFamilies = [];
	@InjectConfig(ModelsExperienceConfigKey.DISABLE_360_LINKAGE) public readonly disableLinkageTo360: boolean = false;

	public fetchModelsList$: Observable<string[]>;
	public familyIds$: Observable<Array<string>>;
	public modelIds$: Observable<Array<string>>;
	public modelIdsList: Array<string>;
	public familyId: string;
	public groupId: string;
	public config = DefaultScrollableHoldingsTableConfig.LATEST_HOLDINGS_TABLE;
	public asOfDate: string;
	public latestHoldingsColumns = [];

	constructor(
		private urlService: UrlService,
		private modelsBrowseLinkActionsService: ModelsBrowseLinkActionsService,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private modelsBrowseMappingService: ModelsBrowseModelsMappingService,
		private tealiumAnalyticsService: TealiumAnalyticsService
	) { }

	public ngOnInit(): void {
		this.groupId = this.urlService.getModelGroupId();
		this.familyId = this.urlService.getModelFamilyId();
		this.latestHoldingsColumns = this.hideLatestAllocationFamilies.includes(this.familyId)
			? this.latestHoldingsHeaderColumns.filter(column => column.field !== 'latestAllocation')
			: this.latestHoldingsHeaderColumns;

		if (this.familyId) {
			this.modelsBrowseDataService.fetchFamilyModelsAnalytics(this.familyId).pipe(untilDestroyed(this)).subscribe(); // to model analytics if not loaded
		}
		else {
			// handled on parent level
			return;
		}
		this.familyIds$ = of([this.familyId]);
		// get the models list if the feature flag is enabled
		this.fetchModelsList$ = this.modelsBrowseMappingService.getModelsList(this.familyId, this.groupId);

		combineLatest([
			this.modelsBrowseDataService.fetchFamilyHoldings(this.familyId),
			this.fetchModelsList$
		]).pipe(untilDestroyed(this))
			.subscribe(([holdings, modelsList]) => {
				const asOfDate = get(holdings[0], 'metadata.currentRebalanceDate');
				if (asOfDate) {
					this.asOfDate = new DateFormatter(this.dateFormat).format(asOfDate);
				}
				// filter holdings based on modelsList
				holdings = modelsList ? holdings.filter(model => modelsList.includes(model.id)) : holdings;
				this.modelIdsList = holdings.map(model => model.id);

				const modelIdsAndConfig = this.modelsBrowseMappingService.setModelIdsAndConfigForComponentData(this.modelIdsList);
				this.setModelIdsAndConfig(modelIdsAndConfig);
			}, error => {
				throw new Error(ErrorCodes.LATEST_HOLDINGS_DATA_ERROR);
			}
			);
	}

	public updateTableDataForSelectedPortfolio(modelIds: string): void {
		this.modelIds$ = of([modelIds]);
	}

	public redirectTo360(portfolioId: string): void {
		if (this.disableLinkageTo360) {
			return;
		}
		const event = TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME + `:latest holdings table:${this.familyId}:${portfolioId}`;
		this.tealiumAnalyticsService.trackEventTealium(
			new TealiumAnalyticsEvent(event, { toolName: TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE })
		);
		this.modelsBrowseLinkActionsService.redirectOnLinkAction(SharedApplicationConstants.PORTFOLIO_ANALYSER_PAGE, ApplicationPathConstants.PA_PATHS.OVERVIEW, portfolioId);
	}

	private setModelIdsAndConfig(modelIdsAndConfig: { modelIds: Observable<string[]>; config: Object }): void {
		this.modelIds$ = modelIdsAndConfig.modelIds;
		this.config.options.rightColClass = modelIdsAndConfig.config['rightColClass'];
		this.config.options.leftColClass = modelIdsAndConfig.config['leftColClass'];
	}

	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		const modelIdsAndConfig = this.modelsBrowseMappingService.setModelIdsAndConfigForComponentData(this.modelIdsList);
		this.setModelIdsAndConfig(modelIdsAndConfig);
	}
}
